import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import ModalShell from '../shared/ModalShell';

const StatusUpdateModal = ({
  isOpen,
  onClose,
  currentStatus,
  onUpdate,
  isUpdating = false,
}) => {
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');

  useEffect(() => {
    if (isOpen) {
      setNewStatus('cancelled');
    }
  }, [isOpen]);

  const isCompleted = currentStatus === 'completed';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newStatus && newStatus !== currentStatus) {
      onUpdate(newStatus, statusReason);
    }
  };

  const handleClose = () => {
    setNewStatus('');
    setStatusReason('');
    onClose();
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={() => !isUpdating && handleClose()}
      labelledBy="cancel-booking-title"
      panelClassName="max-w-md"
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl max-w-md w-full relative overflow-hidden">
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 border-b border-gray-200 dark:border-gray-800">
          <h2 id="cancel-booking-title" className="text-xl font-bold text-gray-900 dark:text-gray-50 mb-2">
            Cancel Booking
          </h2>
          {isCompleted ? (
            <p className="text-sm text-red-600 dark:text-red-400 font-medium">
              This booking is already completed and cannot be cancelled.
            </p>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400">Cancel this booking</p>
          )}
        </div>

        {isCompleted ? (
          <div className="p-6">
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950/50 p-4 text-center">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Completed bookings cannot be modified.
              </p>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 bg-black dark:bg-gray-100 text-white dark:text-gray-900 !rounded-xl hover:bg-gray-900 dark:hover:bg-white transition-colors text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Status
                </label>
                <div className="px-3 py-2 bg-gray-50 dark:bg-gray-950 border border-gray-300 dark:border-gray-600 rounded-xl text-sm text-gray-700 dark:text-gray-300">
                  {currentStatus?.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase()) || 'N/A'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Confirm Cancellation <span className="text-red-500">*</span>
                </label>
                <div className="px-3 py-2 bg-red-50 dark:bg-red-950/40 border border-red-300 dark:border-red-800/50 rounded-xl text-sm text-red-700 dark:text-red-300 font-medium">
                  Cancelled
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason (Optional)
                </label>
                <textarea
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  placeholder="Reason for status change..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500 resize-none"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100 !rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!newStatus || newStatus === currentStatus || isUpdating}
                className="px-4 py-2 bg-black dark:bg-gray-100 text-white dark:text-gray-900 !rounded-xl hover:bg-gray-900 dark:hover:bg-white transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? 'Cancelling...' : 'Cancel Booking'}
              </button>
            </div>
          </form>
        )}
      </div>
    </ModalShell>
  );
};

export default StatusUpdateModal;
